import { QuestionOutlined } from '@ant-design/icons';
import { type CancelToken, createAxiosCancelTokenSource } from '@metservicedev/platforms-services-interface';
import { movIntegrationMDS } from '@metservicedev/platforms-services-interface';
import type { IDataset } from '@metservicedev/platforms-services-interface/src/services/movIntegrationMDS/MovIntegrationMDSModel';
import type { IDatability } from '@mono/types/src/weather-widgets';
import { Button, Cascader, Empty, Select } from 'antd';
import type { BaseOptionType } from 'antd/lib/cascader';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMDSContext } from '@metservice/use-mds';
import { toHash } from '@mono/to-hash';
import { usePlatStageContext } from '@mono/use-plat-stage';
import { capabilitiesByDataTypes } from '../../utils/capabilitiesByDataTypes';
import { DatasetDetailModal } from './DatasetDetailModal';

const StyledAntDivError = styled.div`
    .ant-input {
        ${({ hasError }: { hasError: boolean }) => (hasError ? 'border-color: red !important;' : '')}
    }
    grid-column: 2/6;
`;

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
interface DatasetFormGroupProps {
    passedDownDatasets?: MinimalDataset[];
    setDatasetId(datasetId?: string | null): void;
    dataType: string[];
    datability: IDatability;
    setLoadedAvailableDatasetIds(availableDatasetIds: string[]): void;
    dataStructures?: string[];
    mimeTypes?: string[];
}

export type MinimalDataset = Pick<IDataset, 'id' | 'public_attributes' | 'data_type' | 'display_name_long' | 'display_name_short' | 'temporal_nature'>;

const getDatasetTemporalNatureModelAndId = (dataset: MinimalDataset) => {
    const temporalNature = dataset.temporal_nature;
    const model = dataset.public_attributes?.model || dataset.data_type;
    return {
        id: dataset.id,
        temporalNature,
        model,
    };
};

const DatasetFormGroup: React.FC<DatasetFormGroupProps> = ({
    passedDownDatasets,
    setDatasetId,
    datability,
    setLoadedAvailableDatasetIds,
    dataStructures = ['timeseries/point2d'],
    dataType,
    mimeTypes = ['application/vnd.metocean.cf+json'],
}) => {
    const { capabilities } = useMDSContext();
    const [availableDatasets, setAvailableDatasets] = useState<MinimalDataset[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [datasetsLoading, setDatasetsLoading] = useState(false);
    const [isDatasetModalVisible, setIsDatasetModalVisible] = useState(false);
    const { stage } = usePlatStageContext();
    const { datasetId, space } = datability;
    const capabilityId = datability.capabilityId;
    const selectedDataset = availableDatasets.find(({ id }) => id === datasetId);

    const fetchDatasets = async (
        cancelToken: CancelToken
    ): Promise<{
        availableDatasets: IDataset[];
        errorMessage: string | undefined;
        isDatasetLoaded: boolean;
    }> => {
        try {
            const capabilityIds = capabilityId ? [capabilityId] : capabilitiesByDataTypes(capabilities, dataType).map(({ id }) => id);

            const response = await movIntegrationMDS.http.getDatasets({
                cancelToken,
                metjwt: JSON.parse(localStorage.getItem('okta-token-storage') || '{}')?.accessToken?.accessToken,
                stage,
                headers: { 'X-Current-Org': localStorage.getItem('mip-organisation-id') },
                data: {
                    capabilityIds,
                    space: space!,
                    data_structure: dataStructures,
                    // has_vertical_dimension: true,
                    mime_type: mimeTypes,
                    radius: typeof datability._radius === 'number' ? datability._radius : 1e3,
                    includeFields: ['id', 'public_attributes', 'data_type', 'display_name_long', 'display_name_short', 'temporal_nature'],
                },
            });

            return {
                availableDatasets: response.data,
                errorMessage: undefined,
                isDatasetLoaded: true,
            };
        } catch (e) {
            if (e.userError) {
                return {
                    errorMessage: e.userError,
                    availableDatasets: [],
                    isDatasetLoaded: false,
                };
            } else {
                console.error(e);
                return {
                    availableDatasets: [],
                    errorMessage: undefined,
                    isDatasetLoaded: false,
                };
            }
        }
    };

    useEffect(() => {
        let mounted = true;
        if (capabilities.length === 0) return;

        const controller = createAxiosCancelTokenSource();
        if (space) {
            setDatasetsLoading(true);

            if (passedDownDatasets && passedDownDatasets.length > 0) {
                setAvailableDatasets(passedDownDatasets);
                setLoadedAvailableDatasetIds(passedDownDatasets.map(({ id }) => id));
                setDatasetsLoading(false);
            } else {
                fetchDatasets(controller.token).then((response) => {
                    if (!mounted) {
                        return;
                    }

                    const { errorMessage, isDatasetLoaded, availableDatasets } = response;
                    setErrorMessage(errorMessage);
                    setAvailableDatasets(availableDatasets);

                    if (isDatasetLoaded) {
                        setLoadedAvailableDatasetIds(availableDatasets.map(({ id }) => id));
                    }

                    setDatasetsLoading(false);
                });
            }
        }

        return () => {
            controller.cancel();
            mounted = false;
        };
    }, [capabilityId, passedDownDatasets?.length, toHash(space), datability._radius, capabilities.length]);

    const datasetsGroupedByTemporalNature = useMemo(
        () =>
            availableDatasets.reduce<{ [temporalNature: string]: { [modelOrDataType: string]: MinimalDataset[] } }>((groups, availableDataset) => {
                const { model, temporalNature } = getDatasetTemporalNatureModelAndId(availableDataset);
                return {
                    ...groups,
                    [temporalNature]: {
                        ...(groups[temporalNature] || {}),
                        [model]: [...(groups[temporalNature]?.[model] || []), availableDataset],
                    },
                };
            }, {}),
        [toHash(availableDatasets)]
    );

    const cascaderOptions = Object.keys(datasetsGroupedByTemporalNature).map((datasetGroupName) => ({
        value: datasetGroupName,
        label: capitalize(datasetGroupName),
        children: Object.keys(datasetsGroupedByTemporalNature[datasetGroupName]).map((model) => ({
            value: model,
            label: model.toUpperCase(),
            children: datasetsGroupedByTemporalNature[datasetGroupName][model].map((dataset) => ({
                value: dataset.id,
                label: capitalize(dataset.display_name_long || dataset.display_name_short).replace(model.toUpperCase(), ''),
            })),
        })),
    }));

    function filter(inputValue: string, path: BaseOptionType[]) {
        return !!path.find((option) => (option.label as string)?.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
    }

    return (
        <>
            <div style={{ gridColumn: 1 }}>
                <strong>
                    Dataset:
                    {selectedDataset && (
                        <Button
                            style={{ color: 'white', marginLeft: 2 }}
                            onClick={() => setIsDatasetModalVisible(true)}
                            size='small'
                            icon={
                                <QuestionOutlined
                                    style={{
                                        color: 'rgba(0,0,0,.85)',
                                    }}
                                />
                            }
                            shape='circle'
                            type='ghost'
                        />
                    )}
                </strong>
            </div>
            {isDatasetModalVisible && <DatasetDetailModal datasetId={datasetId} close={() => setIsDatasetModalVisible(false)} />}
            <StyledAntDivError hasError={!!errorMessage}>
                {datasetsLoading ? (
                    <Select placeholder={datasetId} loading={true} disabled={true} style={{ width: '100%' }} />
                ) : (
                    <Cascader
                        value={
                            selectedDataset
                                ? [
                                      getDatasetTemporalNatureModelAndId(selectedDataset).temporalNature,
                                      getDatasetTemporalNatureModelAndId(selectedDataset).model,
                                      selectedDataset.id,
                                  ]
                                : undefined
                        }
                        placeholder={
                            datasetsLoading ? (datasetId ? `Loading ${datasetId}` : 'Loading available datasets...') : errorMessage || 'Please select a dataset'
                        }
                        disabled={datasetsLoading}
                        notFoundContent={errorMessage && <Empty description={errorMessage} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                        style={{ width: '100%' }}
                        onChange={(val: string[]) => {
                            if (!val) {
                                setDatasetId(null);
                                return;
                            }
                            setDatasetId(val[val.length - 1]);
                        }}
                        showSearch={{ filter }}
                        options={cascaderOptions}
                        expandTrigger='hover'
                        allowClear={!!datasetId}
                    />
                )}
            </StyledAntDivError>
        </>
    );
};

export default React.memo(DatasetFormGroup);
